/**
 * Parts the client-review dialogs share (Send to the client, Record an
 * approval; chapter 08 frames pDm4v, xaubY, FKvo5). The desk form is the
 * library's Dialog: floating chrome at radius 16, a Barlow Condensed 800 title
 * in sentence case, a muted description, a close circle, and Cancel (ghost)
 * then the primary in the foot. On a phone it is a sheet 8px in from the
 * sides and the foot, with a grabber and a Manrope 17 title.
 */
import type { ReactNode } from "react";
import {
  Button as AriaButton,
  DatePicker,
  Dialog,
  Group,
  Heading,
  Modal,
  ModalOverlay,
} from "react-aria-components";
import { Calendar, ChevronDown, X } from "lucide-react";
import { cn } from "@/utils/helpers";
import DatePickerCalendar from "@/components/form/DatePickerCalendar";
import { parseIsoDate } from "@/components/form/dateInputUtils";
import { useCoShellPortal } from "../../CoShellPortalContext";
import { KitPopover } from "../../components/kit/Popover";

/** The section label: JetBrains Mono, tracked; capitals on a desk, sentence case on a phone. */
export function DialogLabel({ children, aside, phone }: { children: ReactNode; aside?: ReactNode; phone?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span className={cn("font-mono text-[10.5px] text-co-muted", phone ? "tracking-[0.11em]" : "uppercase tracking-[0.13em]")}>
        {children}
      </span>
      {aside != null ? (
        <>
          <span className="flex-1" />
          <span className={cn("font-mono text-co-faint", phone ? "text-[10.5px] tracking-[0.11em]" : "text-[10px] uppercase tracking-[0.13em]")}>
            {aside}
          </span>
        </>
      ) : null}
    </div>
  );
}

export function ReviewDialog({
  isOpen,
  onOpenChange,
  title,
  description,
  phone,
  width = 640,
  footer,
  children,
  dismissable = true,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  phone: boolean;
  width?: number;
  footer: ReactNode;
  children: ReactNode;
  dismissable?: boolean;
}) {
  const portal = useCoShellPortal();
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={dismissable}
      isKeyboardDismissDisabled={!dismissable}
      UNSTABLE_portalContainer={portal ?? undefined}
      className={({ isEntering, isExiting }) =>
        cn(
          "fixed inset-0 z-[var(--z-index-modal)] flex bg-co-scrim",
          phone ? "items-end justify-center p-2" : "items-center justify-center p-4",
          isEntering && "animate-in-smooth fade-in",
          isExiting && "animate-out-slow fade-out",
        )
      }
    >
      <Modal
        style={phone ? undefined : { width }}
        className={({ isEntering, isExiting }) =>
          cn(
            "co-glass-chrome flex max-h-[calc(100dvh-32px)] min-h-0 flex-col overflow-hidden rounded-panel text-co-text outline-none",
            phone ? "max-h-[calc(100dvh-16px)] w-[374px] max-w-full" : "max-w-full",
            isEntering && (phone ? "animate-in-smooth sheet-in" : "animate-in-smooth dialog-in"),
            isExiting && (phone ? "animate-out-slow sheet-out" : "animate-out-slow dialog-out"),
          )
        }
      >
        <Dialog className="flex min-h-0 flex-1 flex-col outline-none">
          {phone ? (
            <>
              <div aria-hidden className="flex h-[18px] shrink-0 items-center justify-center">
                <span className="h-1 w-9 rounded-[2px] bg-co-faint" />
              </div>
              <div className="flex shrink-0 items-center py-1 pl-4 pr-2">
                <Heading slot="title" className="flex-1 font-body text-[17px] font-semibold text-co-text">
                  {title}
                </Heading>
                <CloseCircle size={44} onPress={() => onOpenChange(false)} />
              </div>
            </>
          ) : (
            <div className="flex shrink-0 items-start justify-between gap-4 px-6 pb-2 pt-6">
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <Heading slot="title" className="font-section text-[20px] font-extrabold leading-[1.1] tracking-[0.015em] text-co-text">
                  {title}
                </Heading>
                {description ? <p className="text-[13px] leading-[1.55] text-co-muted">{description}</p> : null}
              </div>
              <CloseCircle size={32} onPress={() => onOpenChange(false)} />
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {phone && description ? (
              <p className="px-4 pt-4 text-[13px] leading-[1.5] text-co-text2">{description}</p>
            ) : null}
            {children}
          </div>
          {footer}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

function CloseCircle({ size, onPress }: { size: number; onPress: () => void }) {
  return (
    <button
      type="button"
      aria-label="Close"
      onClick={onPress}
      style={{ width: size, height: size }}
      className="grid shrink-0 place-items-center rounded-full text-co-muted outline-none transition-colors hover:bg-co-hover hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent"
    >
      <X aria-hidden className="size-3.5" strokeWidth={1.75} />
    </button>
  );
}

/** "2026-12-01" → "Tue 1 Dec"; with `today`, "Today, Thu 3 Dec". */
export function dayLabel(iso: string, todayIso?: string): string {
  const date = parseIsoDate(iso);
  if (!date) return "";
  const js = new Date(Date.UTC(date.year, date.month - 1, date.day));
  const weekday = js.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" });
  const month = js.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" }).replace("Sept", "Sep");
  const label = `${weekday} ${date.day} ${month}`;
  return todayIso && todayIso === iso ? `Today, ${label}` : label;
}

/** Today in the reader's zone, as YYYY-MM-DD. */
export function localTodayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

/**
 * Date field (library): a 44px control-glass field at radius 12 with the
 * calendar icon, the day in words and a chevron; it opens the date picker.
 */
export function DayField({
  value,
  onChange,
  minValue,
  maxValue,
  label,
  placeholder = "Pick a day",
  className,
  showToday = false,
  icon = true,
}: {
  value: string;
  onChange: (value: string) => void;
  minValue?: string;
  maxValue?: string;
  label: string;
  placeholder?: string;
  className?: string;
  showToday?: boolean;
  icon?: boolean;
}) {
  const parsed = parseIsoDate(value);
  return (
    <DatePicker
      aria-label={label}
      value={parsed}
      onChange={(date) => onChange(date ? date.toString() : "")}
      minValue={parseIsoDate(minValue) ?? undefined}
      maxValue={parseIsoDate(maxValue) ?? undefined}
      shouldCloseOnSelect
      className={cn("min-w-0", className)}
    >
      <Group className="w-full">
        <AriaButton
          className={cn(
            "co-glass-control flex h-11 w-full items-center gap-2.5 rounded-card px-3.5 text-left text-[14px] font-medium text-co-text outline-none",
            "data-[focus-visible]:outline-2 data-[focus-visible]:outline-co-accent data-[pressed]:outline-2 data-[pressed]:outline-co-accent",
          )}
        >
          {icon ? <Calendar aria-hidden className="size-4 shrink-0 text-co-muted" strokeWidth={1.75} /> : null}
          <span className={cn("flex-1 truncate", !value && "font-normal text-co-faint")}>
            {value ? dayLabel(value, showToday ? localTodayIso() : undefined) : placeholder}
          </span>
          {icon ? <ChevronDown aria-hidden className="size-3.5 shrink-0 text-co-muted" strokeWidth={1.75} /> : null}
        </AriaButton>
      </Group>
      <KitPopover placement="bottom start" offset={8} className="co-calendar-panel overflow-hidden p-0">
        <Dialog className="outline-none">
          <DatePickerCalendar />
        </Dialog>
      </KitPopover>
    </DatePicker>
  );
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

/** Avatar (library): 24, round, tonal, initials in Manrope 700. */
export function Initials({ name, size = 24, fontSize = 12 }: { name: string; size?: number; fontSize?: number }) {
  return (
    <span
      aria-hidden
      style={{ width: size, height: size, fontSize }}
      className="grid shrink-0 place-items-center rounded-full bg-co-tonal font-bold leading-none text-co-text2"
    >
      {initialsOf(name)}
    </span>
  );
}

/** The error or warning line a dialog shows above its foot. */
export function DialogNotice({ tone, title, detail }: { tone: "error" | "warning"; title: string; detail: string }) {
  return (
    <div
      role="alert"
      className={cn(
        "rounded-card px-4 py-3",
        tone === "error"
          ? "bg-[color-mix(in_srgb,var(--co-danger)_10%,transparent)]"
          : "bg-[color-mix(in_srgb,var(--co-alert-ink)_10%,transparent)]",
      )}
    >
      <p className="text-[13.5px] font-semibold text-co-text">{title}</p>
      <p className="mt-1 text-[12.5px] leading-[1.5] text-co-text2">{detail}</p>
    </div>
  );
}
